import React, { useEffect, useState } from "react";
import { Button, Col, Tabs, Tab } from "react-bootstrap";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import ProductVariationDropzone from "../../../../components/Dropzone/ProductVariationDropzone";
import toast from "react-hot-toast";
import { LuSalad } from "react-icons/lu";
import { IoStorefrontOutline } from "react-icons/io5";
import {
  useAddProductBannerMutation,
  useDeleteProductBannerMutation,
  useGetProductBannerQuery,
  useUpdateProductBannerMutation,
} from "../../../../store/services/ProductBanner";
import {
  useAddDeliveryInformationMutation,
  useUpdateDeliveryInformationMutation,
  useGetDeliveryInformationQuery,
} from "../../../../store/services/DeliveryInformation";
import { useFormik } from "formik";
import * as Yup from "yup";
import CKEditorNew from "../../../../components/newCkEditor/ckEditor";

const isVideo = (filePath) => {
  if (!filePath) return false;
  const videoExtensions = ["mp4", "3gp", "mkv"];
  const fileExtension = filePath?.split(".").pop()?.toLowerCase();
  return videoExtensions.includes(fileExtension);
};

const TAGLINE_WORD_LIMIT = 30;

function ProductsSettingsForm() {
  const [taglineFields, setTaglineFields] = useState([]);
  const [bannerFields, setBannerFields] = useState([]);
  const [mobileBannerFields, setMobileBannerFields] = useState([]);
  const [addTaglineField, setAddTaglineField] = useState("");
  const [activeTab, setActiveTab] = useState("storeSettings");
  const { data, isLoading } = useGetProductBannerQuery();
  const { data: deliveryData, isLoading: isDeliveryLoading } =
    useGetDeliveryInformationQuery(undefined, {
      skip: activeTab !== "deliveryInfo",
    });
  const { settingData, isSettingLoading } = useGetDeliveryInformationQuery();
  useEffect(() => {
    if (data?.data?.[0]?.tagline) setTaglineFields(data?.data?.[0]?.tagline);
    if (data?.data?.[0]?.banner) setBannerFields(data?.data?.[0]?.banner);
    if (data?.data?.[0]?.mobileBanner)
      setMobileBannerFields(data?.data?.[0]?.mobileBanner);
  }, [isLoading]);

  //Meals and Store
  const [addSettings, { isLoading: isSettingAdding }] =
    useAddDeliveryInformationMutation();
  const [updateSettings, { isLoading: isSettingUpdating }] =
    useUpdateDeliveryInformationMutation();
  //banner and tagline
  const [updateProductBanner, { isLoading: isUpdating }] =
    useUpdateProductBannerMutation();
  const [addProductBanner, { isLoading: isAdding }] =
    useAddProductBannerMutation();

  const deliveryInfo = deliveryData?.data?.[0] || null;

  const onSubmit = async () => {
    const body = {
      banner: bannerFields || [],
      tagline: taglineFields || [],
      mobileBanner: mobileBannerFields || [],
    };
    try {
      const existingId = data?.data?.[0]?._id;

      if (existingId) {
        await updateProductBanner({
          updatedData: body,
          id: existingId,
        }).unwrap();
      } else {
        await addProductBanner(body).unwrap();
      }

      toast.success("Product settings saved successfully!");
    } catch (err) {
      toast.error("Failed to save product settings.");
    }
  };

  const validateImageDimensions = (src, requiredWidth, requiredHeight) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        const isValid = this.naturalWidth === requiredWidth && this.naturalHeight === requiredHeight;
        resolve(isValid);
      };
      image.onerror = function () {
        resolve(false);
      };
      image.src = src;
    });
  };

  const deliveryFormik = useFormik({
    initialValues: {
      mealPlanDelivery: deliveryInfo?.mealPlanDelivery || "",
      productDelivery: deliveryInfo?.productDelivery || "",
    },
    validationSchema: Yup.object({
      mealPlanDelivery: Yup.string().required("Meal Plan Delivery is required"),
      productDelivery: Yup.string().required("Product Delivery is required"),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!values.productDelivery || !values.mealPlanDelivery) {
          toast.error("All fields are required.");
          setSubmitting(false);
          return;
        }
        if (!deliveryInfo) {
          await addSettings(values).unwrap();
          toast.success("Delivery Information Added!");
        } else {
          await updateSettings({
            updatedData: values,
            id: deliveryInfo._id,
          }).unwrap();
          toast.success("Delivery Information Updated!");
        }
      } catch (err) {
        toast.error("Failed to save delivery information.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddTagline = () => {
    const newTagline = addTaglineField;
    if (taglineFields.length >= 20) {
      toast.error("You can only add up to 20 taglines.");
      return;
    }
    if (!newTagline || newTagline.trim().length === 0) {
      toast.error("Tagline cannot be empty or just spaces.");
      return;
    }
    if (newTagline.length > 250) {
      toast.error("Tagline cannot exceed 250 characters.");
      return;
    }
    setTaglineFields((prev) => [...prev, newTagline]);
    setAddTaglineField("");
  };

  return (
    <Col lg="8" md="10" sm="12" className="my-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Tabs
          activeKey={activeTab}
          onSelect={setActiveTab}
          id="product-settings-tabs"
          className="mb-3"
        >
          <Tab eventKey="storeSettings" title="Store Settings">
            {/* Tagline Inputs - support multiple taglines */}
            <div className="mb-4">
              <label className="form-label fw-sbold text-muted ps-2 m-0">
                Taglines
              </label>
              <div className="d-flex align-items-center mb-2 gap-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Enter product tagline`}
                  style={{ width: "70%" }}
                  value={addTaglineField}
                  onChange={(e) => setAddTaglineField(e.target.value)}
                />
                <Button
                  className="d-flex align-items-center justify-content-center commonBtn"
                  size="sm"
                  onClick={handleAddTagline}
                  type="button"
                  style={{ alignSelf: "center" }}
                >
                  Add Tagline
                </Button>
              </div>
              {taglineFields.map((field, index) => (
                <div key={field} className="d-flex align-items-center mb-2">
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    value={field}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const wordCount = newValue
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean).length;
                      if (wordCount > TAGLINE_WORD_LIMIT) {
                        toast.error(
                          `Tagline cannot exceed ${TAGLINE_WORD_LIMIT} words.`
                        );
                        return;
                      }
                      setTaglineFields((prev) =>
                        prev?.map((data, i) => (i === index ? newValue : data))
                      );
                    }}
                    placeholder={`Enter product tagline #${index + 1}`}
                  />
                  <Button
                    className="d-flex align-items-center justify-content-center commonBtn GreyBtn ms-2"
                    size="sm"
                    onClick={() =>
                      setTaglineFields((prev) =>
                        prev.filter((data, i) => index !== i)
                      )
                    }
                    type="button"
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            <div
              className="formWrpper px-lg-5 p-md-4 p-3 rounded"
              style={{ background: "#EEEEEE" }}
            >
              <div className="mb-4">
                <label className="form-label fw-sbold text-muted ps-2 m-0">
                  Banners (Images or Videos) — Web 2000 × 750
                </label>
                <div className="flex-grow-1">
                  <ProductVariationDropzone
                    field="productBanners"
                    requiredImageSize={{ width: 2000, height: 750 }}
                    allowVideos={true}
                    setFieldValue={(_, urls) => {
                      if (!Array.isArray(urls) || urls.length === 0) return;
                      setBannerFields((prev) => [...prev, ...urls]);
                    }}
                  />
                </div>
                {bannerFields.map((field, index) => (
                  <div key={field.id} className="mb-3">
                    <div className="d-flex align-items-start">
                      {isVideo(field) ? (
                        <video
                          width={"100%"}
                          height={120}
                          controls
                          autoPlay={true}
                          loop
                          src={field}
                          className="video_preview"
                        >
                          {/* <source src={value} type={"video/mp4"} /> */}
                        </video>
                      ) : (
                        <img
                          className="rounded avatar-xl"
                          alt={""}
                          src={
                            field ? field : "/assets/img/logo-placeholder.png"
                          }
                          width={"100%"}
                          height={"150px"}
                          style={{
                            objectFit: "contain",
                            background: "#e9eef1",
                          }}
                        />
                      )}
                      <Button
                        className="d-flex align-items-center justify-content-center commonBtn GreyBtn mt-2"
                        size="sm"
                        onClick={() =>
                          setBannerFields((prev) =>
                            prev.filter((data, i) => index !== i)
                          )
                        }
                        // disabled={bannerFields.length === 1}
                        type="button"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-4">
                <label className="form-label fw-sbold text-muted ps-2 m-0">
                  Mobile Banners (Images) — Mobile 575 × 718
                </label>
                <div className="flex-grow-1">
                  <ProductVariationDropzone
                    field="productMobileBanners"
                    requiredImageSize={{ width: 575, height: 718 }}
                    allowVideos={false}
                    setFieldValue={(_, urls) => {
                      if (!Array.isArray(urls) || urls.length === 0) return;
                      setMobileBannerFields((prev) => [...prev, ...urls]);
                    }}
                  />
                </div>
                {mobileBannerFields.map((field, index) => (
                  <div key={field} className="mb-3">
                    <div className="d-flex align-items-start">
                      <img
                        className="rounded avatar-xl"
                        alt={""}
                        src={field ? field : "/assets/img/logo-placeholder.png"}
                        width={"100%"}
                        height={"150px"}
                        style={{
                          objectFit: "contain",
                          background: "#e9eef1",
                        }}
                      />
                      <Button
                        className="d-flex align-items-center justify-content-center commonBtn GreyBtn mt-2"
                        size="sm"
                        onClick={() =>
                          setMobileBannerFields((prev) =>
                            prev.filter((data, i) => index !== i)
                          )
                        }
                        type="button"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-right my-3">
              <Button
                type="submit"
                className="d-flex align-items-center justify-content-center commonBtn"
                disabled={isUpdating || isAdding}
              >
                {isUpdating || isAdding ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </Tab>
          <Tab eventKey="deliveryInfo" title="Delivery Information">
            <form onSubmit={deliveryFormik.handleSubmit}>
              <div className="p-3 rounded" style={{ background: "#EEEEEE", minHeight: "350px", padding: "2rem", width: "100%" }}>
                <h5 className="fw-bold mb-3">Delivery Information</h5>
                <div className="mb-3">
                  <label className="form-label fw-sbold text-muted ps-2 m-0">
                    Meal Plan Delivery
                  </label>
                  <CKEditorNew
                    field="mealPlanDelivery"
                    setFieldValue={(field, value) => {
                      deliveryFormik.setFieldValue("mealPlanDelivery", value);
                    }}
                    value={deliveryFormik.values.mealPlanDelivery}
                    errors={deliveryFormik.errors}
                    placeholder="Enter Meal Plan Delivery info"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted">
                      {/* No character limit for Meal Plan Delivery */}
                    </small>
                    {deliveryFormik.touched.mealPlanDelivery && deliveryFormik.errors.mealPlanDelivery && (
                      <div className="text-danger small">
                        {deliveryFormik.errors.mealPlanDelivery}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-sbold text-muted ps-2 m-0">
                    Product Delivery
                  </label>
                  <CKEditorNew
                    field="productDelivery"
                    setFieldValue={(field, value) => {
                      deliveryFormik.setFieldValue("productDelivery", value);
                    }}
                    value={deliveryFormik.values.productDelivery}
                    errors={deliveryFormik.errors}
                    placeholder="Enter Product Delivery info"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted">
                      {/* No character limit for Product Delivery */}
                    </small>
                    {deliveryFormik.touched.productDelivery && deliveryFormik.errors.productDelivery && (
                      <div className="text-danger small">
                        {deliveryFormik.errors.productDelivery}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right my-3">
                  <Button
                    type="button"
                    onClick={(e) => deliveryFormik.handleSubmit()}
                    className="d-flex align-items-center justify-content-center commonBtn"
                    disabled={
                      deliveryFormik.isSubmitting ||
                      isDeliveryLoading ||
                      isSettingAdding ||
                      isSettingUpdating
                    }
                  >
                    {deliveryInfo
                      ? isSettingUpdating || deliveryFormik.isSubmitting
                        ? "Updating..."
                        : "Update Delivery Info"
                      : isSettingAdding || deliveryFormik.isSubmitting
                      ? "Adding..."
                      : "Add Delivery Info"}
                  </Button>
                </div>
              </div>
            </form>
          </Tab>
        </Tabs>
        {/* Submit Button */}
      </form>
    </Col>
  );
}

export default ProductsSettingsForm;
